import {
    CloudData,
    RegionalPipelineData,
    HealthStatus,
    EventData,
    Pipeline,
    WorkLane,
    Region,
} from '../models/CloudStatusModels';
import {
    CloudStatusSummary,
    ComponentEvent,
    EnvironmentData,
    EnvironmentDataResponse,
} from '../models/CloudStatusApiModels';
import * as sdsApi from '../api/SDSApi';
import * as agcpApi from '../../AGCP/api/AGCPApi';

/**
 * Fetch lightweight cloud list (names and overall health only)
 */
export const fetchCloudList = async (): Promise<CloudStatusSummary[]> => {
    try {
        const response = await sdsApi.getCloudList();

        if (response.success && response.clouds && response.clouds.length > 0) {
            return response.clouds;
        } else {
            console.warn('API returned no cloud list');
            return [];
        }
    } catch (error) {
        console.error('Error fetching cloud list from API:', error);
        return [];
    }
};

/**
 * Fetch full cloud status data for a specific cloud
 * @param cloudName The name of the cloud to fetch
 */
export const fetchCloudStatusByName = async (
    cloudName: string
): Promise<CloudData | undefined> => {
    try {
        // Call the API for a specific cloud with full details
        const response = await sdsApi.getCloudStatusByName(
            cloudName,
            true,
            true,
            false
        );

        if (response.success && response.clouds && response.clouds.length > 0) {
            return convertApiCloudToCloudData(response.clouds[0]);
        } else {
            console.warn(`API returned no data for cloud ${cloudName}`);
            return undefined;
        }
    } catch (error) {
        console.error(
            `Error fetching cloud status for ${cloudName} from API:`,
            error
        );
        return undefined;
    }
};

/**
 * Fetch events for a specific component
 * @param componentGuid The component GUID to get events for
 * @param maxEventAgeHours Maximum age of events to include in hours (default: 24)
 * @param maxEvents Maximum number of events to return (default: 50)
 */
export const fetchComponentEvents = async (
    componentGuid: string,
    maxEventAgeHours: number = 24,
    maxEvents: number = 50
): Promise<EventData[]> => {
    try {
        const response = await sdsApi.getComponentEvents(
            componentGuid,
            maxEventAgeHours,
            maxEvents
        );

        if (response.success && response.events && response.events.length > 0) {
            // Convert API events to UI EventData format
            return response.events.map(convertApiEventToEventData);
        } else {
            console.warn(
                `API returned no events for component ${componentGuid}`
            );
            return [];
        }
    } catch (error) {
        console.error(
            `Error fetching events for component ${componentGuid}:`,
            error
        );
        return [];
    }
};

/**
 * Convert API event to UI EventData format
 */
const convertApiEventToEventData = (apiEvent: ComponentEvent): EventData => {
    return {
        eventId: apiEvent.eventId,
        environmentName: apiEvent.environmentName,
        eventType: apiEvent.eventType,
        serviceSourceGuid: apiEvent.serviceSourceGuid,
        componentGuid: apiEvent.componentGuid,
        componentIndex: apiEvent.componentIndex,
        statusGuid: apiEvent.statusGuid,
        statusIndicatorEnum: apiEvent.statusIndicatorEnum,
        trueValue: apiEvent.trueValue,
        additionalValue: apiEvent.additionalValue,
        errorCode: apiEvent.errorCode,
        health: apiEvent.health,
        eventDate: new Date(apiEvent.eventDate),
    };
};

/**
 * Convert API cloud data to UI CloudData format
 */
const convertApiCloudToCloudData = (apiCloud: any): CloudData => {
    // The API returns data in a different format - need to convert stages from array to object
    const convertedPipelines = (apiCloud.pipelines || []).map(
        (pipeline: any) => {
            // Convert stages array to object with named keys
            const stagesObject: any = {};
            const connectionsObject: any = {};

            // Create stages object from array
            if (pipeline.stages && Array.isArray(pipeline.stages)) {
                pipeline.stages.forEach((stage: any) => {
                    const stageKey = getStageNameKey(stage.stage);
                    if (stageKey) {
                        stagesObject[stageKey] = {
                            name: stage.name,
                            status: stage.status,
                            owner: stage.owner,
                            cloudOwnership: stage.cloudOwnership,
                        };
                    }
                });
            }

            // Convert connections array to object
            if (pipeline.connections && Array.isArray(pipeline.connections)) {
                pipeline.connections.forEach((conn: any) => {
                    const connKey = getConnectionKey(
                        conn.fromStage,
                        conn.toStage
                    );
                    if (connKey) {
                        connectionsObject[connKey] = {
                            status: conn.status,
                        };
                    }
                });
            }

            const convertedPipeline = {
                flowId: pipeline.flowId || '',
                pipeline: convertEnumStringToNumber(pipeline.pipeline, 'pipeline'),
                workLane: convertEnumStringToNumber(pipeline.workLane, 'workLane'),
                region: convertEnumStringToNumber(pipeline.region, 'region'),
                ctsType: pipeline.ctsType || pipeline.metadata?.CtsType || '',
                direction: pipeline.direction || pipeline.metadata?.Direction || '',
                sourceRegion: pipeline.sourceRegion || pipeline.metadata?.SourceRegion || '',
                source: pipeline.source || pipeline.metadata?.Source || '',
                destination: pipeline.destination || pipeline.metadata?.Destination || '',
                destinationRegion: pipeline.destinationRegion || pipeline.metadata?.DestinationRegion || '',
                primaryRegion: pipeline.primaryRegion,
                secondaryRegion: pipeline.secondaryRegion,
                isActive: pipeline.isActive ?? true,
                stages: stagesObject,
                connections: connectionsObject,
                metadata: pipeline.metadata,
            };

            return convertedPipeline;
        }
    );

    return {
        ...apiCloud,
        pipelines: convertedPipelines,
        services: (apiCloud.services || []).map((service: any) => ({
            ...service,
            lastUpdated: service.lastUpdated
                ? new Date(service.lastUpdated)
                : new Date(),
            components: (service.components || []).map((comp: any) => ({
                componentId: comp.componentGuid, // Map componentGuid from API to componentId for UI
                componentName: comp.componentName,
                health: comp.health,
                lastUpdated: comp.lastUpdated
                    ? new Date(comp.lastUpdated)
                    : new Date(),
            })),
            // Return empty events list for now
            events: [],
        })),
    };
};

// Helper to convert string enum values to numeric enum values
const convertEnumStringToNumber = (enumValue: string | number, enumType: 'pipeline' | 'workLane' | 'region'): number => {
    // If already a number, return it
    if (typeof enumValue === 'number') {
        return enumValue;
    }

    // Convert string enum names to numeric values
    if (enumType === 'pipeline') {
        const pipelineMap: { [key: string]: number } = {
            'Blob': Pipeline.Blob,
            'Core': Pipeline.Core,
            'Message': Pipeline.Message,
        };
        return pipelineMap[enumValue] ?? 0;
    } else if (enumType === 'workLane') {
        const workLaneMap: { [key: string]: number } = {
            'Warm': WorkLane.Warm,
            'Dcmt': WorkLane.Dcmt,
        };
        return workLaneMap[enumValue] ?? 0;
    } else if (enumType === 'region') {
        const regionMap: { [key: string]: number } = {
            'Primary': Region.Primary,
            'Secondary': Region.Secondary,
        };
        return regionMap[enumValue] ?? 0;
    }

    return 0;
};

// Helper to map stage enum to object key
const getStageNameKey = (stageEnum: string): string | null => {
    const stageMap: { [key: string]: string } = {
        Public: 'public',
        PublicTransfer: 'publicTransfer',
        CloudArrive: 'cloudArrive',
        CloudEngine: 'cloudEngine',
        CloudPlatform: 'cloudPlatform',
    };
    return stageMap[stageEnum] || null;
};

// Helper to map connection stages to object key
const getConnectionKey = (
    fromStage: string,
    toStage: string
): string | null => {
    const connectionMap: { [key: string]: string } = {
        Public_PublicTransfer: 'publicToPublicTransfer',
        PublicTransfer_CloudArrive: 'publicTransferToCloudArrive',
        CloudArrive_CloudEngine: 'cloudArriveToCloudEngine',
    };
    const key = `${fromStage}_${toStage}`;
    return connectionMap[key] || null;
};

// Calculate overall health based on pipeline statuses
export const calculateOverallHealth = (
    pipelines: RegionalPipelineData[]
): HealthStatus => {
    const hasUnhealthy = pipelines.some(
        (p) =>
            p.stages.public.status === 'Unhealthy' ||
            p.stages.publicTransfer.status === 'Unhealthy' ||
            p.stages.cloudArrive.status === 'Unhealthy' ||
            p.stages.cloudEngine.status === 'Unhealthy' ||
            p.stages.cloudPlatform.status === 'Unhealthy' ||
            p.connections.publicToPublicTransfer.status === 'Unhealthy' ||
            p.connections.publicTransferToCloudArrive.status === 'Unhealthy' ||
            p.connections.cloudArriveToCloudEngine.status === 'Unhealthy'
    );

    if (hasUnhealthy) return 'Unhealthy';

    const hasDegraded = pipelines.some(
        (p) =>
            p.stages.public.status === 'Degraded' ||
            p.stages.publicTransfer.status === 'Degraded' ||
            p.stages.cloudArrive.status === 'Degraded' ||
            p.stages.cloudEngine.status === 'Degraded' ||
            p.stages.cloudPlatform.status === 'Degraded' ||
            p.connections.publicToPublicTransfer.status === 'Degraded' ||
            p.connections.publicTransferToCloudArrive.status === 'Degraded' ||
            p.connections.cloudArriveToCloudEngine.status === 'Degraded'
    );

    if (hasDegraded) return 'Degraded';

    return 'Healthy';
};

/**
 * Fetch environment configuration data for a specific environment
 * @param environmentName The name of the environment (e.g., "development", "production")
 * @returns Mock environment data until API is connected
 */
export const fetchEnvironmentData = async (
    environmentName: string
): Promise<EnvironmentData | undefined> => {
    try {
        // TODO: Uncomment when ready to connect real API
        // const response = await agcpApi.getEnvironment(environmentName);
        // if (response.succeeded && response.data) {
        //     return response.data;
        // }

        // Return mock data for now
        return getMockEnvironmentData(environmentName);
    } catch (error) {
        console.error(
            `Error fetching environment data for ${environmentName}:`,
            error
        );
        // Return mock data on error
        return getMockEnvironmentData(environmentName);
    }
};

/**
 * Get mock environment data for development/testing
 */
const getMockEnvironmentData = (environmentName: string): EnvironmentData => {
    return {
        name: environmentName,
        activeOperationalModeName: 'Normal',
        normalOperationMode: {
            name: 'Normal',
            isEnabled: true,
            region: undefined,
            pipeline: Pipeline.Core,
            worklane: WorkLane.Warm,
            channels: [
                {
                    name: 'normal-core-primary',
                    pipeline: Pipeline.Core,
                    region: Region.Primary,
                    workLane: WorkLane.Warm,
                    isActive: true,
                },
                {
                    name: 'priority-core-primary',
                    pipeline: Pipeline.Core,
                    region: Region.Primary,
                    workLane: WorkLane.Dcmt,
                    isActive: true,
                },
                {
                    name: 'normal-blob-primary',
                    pipeline: Pipeline.Blob,
                    region: Region.Primary,
                    workLane: WorkLane.Warm,
                    isActive: true,
                },
                {
                    name: 'priority-blob-primary',
                    pipeline: Pipeline.Blob,
                    region: Region.Primary,
                    workLane: WorkLane.Dcmt,
                    isActive: true,
                },
                {
                    name: 'normal-core-secondary',
                    pipeline: Pipeline.Core,
                    region: Region.Secondary,
                    workLane: WorkLane.Warm,
                    isActive: true,
                },
                {
                    name: 'priority-core-secondary',
                    pipeline: Pipeline.Core,
                    region: Region.Secondary,
                    workLane: WorkLane.Dcmt,
                    isActive: true,
                },
                {
                    name: 'normal-blob-secondary',
                    pipeline: Pipeline.Blob,
                    region: Region.Secondary,
                    workLane: WorkLane.Warm,
                    isActive: true,
                },
                {
                    name: 'priority-blob-secondary',
                    pipeline: Pipeline.Blob,
                    region: Region.Secondary,
                    workLane: WorkLane.Dcmt,
                    isActive: true,
                },
            ],
            dataChannelOverrides: [],
            featureFlags: {
                useDataChannelOverrides: true,
                cachingEnabled: true,
                messagingEnabled: true,
                storageUploadEnabled: true,
                octopusEnabled: true,
                lockBoxEnabled: true,
                egressEnabled: true,
                icmEnabled: true,
                useDynamicHealthChecks: false,
                useSrxV2: true,
            },
            featureFlagsOverrides: [],
        },
        operationModes: [
            {
                name: 'DisableCache',
                isEnabled: true,
                region: undefined,
                pipeline: undefined,
                worklane: undefined,
                channels: [],
                dataChannelOverrides: [],
                featureFlags: {
                    cachingEnabled: false,
                },
                featureFlagsOverrides: [],
            },
            {
                name: 'FailToSecondary',
                isEnabled: true,
                region: Region.Secondary,
                pipeline: undefined,
                worklane: undefined,
                channels: [],
                dataChannelOverrides: [],
                featureFlags: {
                    useDataChannelOverrides: false,
                },
                featureFlagsOverrides: [],
            },
        ],
    };
};
